/**
 * Runtime identity channel (specs/common/deployment/15 §5; 99 D31).
 *
 * Environment-dependent identity inputs are never baked into the bundle
 * (single-build discipline, CF12): deployed surfaces — the Cloudflare edge
 * worker and the bare nginx vhost — serve them from the reserved same-origin
 * path `/.zero/runtime.json`, and the auth strategy reads the issuer from
 * there before first token validation. Local vite dev has no such surface;
 * a missing or malformed channel falls back to the caller's page-origin
 * default (fail-visible: a central-issuer token then fails `iss` validation).
 */

/** Reserved runtime-config path; `/.zero/` is a reserved SPA namespace. */
export const RUNTIME_CONFIG_PATH = "/.zero/runtime.json";

export interface RuntimeConfig {
  /** The environment's central IDAAS issuer (absolute http(s) origin). */
  readonly idaasIssuer?: string;
  /** Environment label (`test` | `production`); informational for SPAs. */
  readonly environment?: string;
}

const EMPTY_RUNTIME_CONFIG: RuntimeConfig = Object.freeze({});
const MAX_FIELD_BYTES = 256;
const FETCH_TIMEOUT_MS = 5_000;

/**
 * Fetch the runtime config from the page's own origin. Every failure mode —
 * non-200, malformed body, invalid field shape, network/timeout error, or a
 * synchronously throwing `fetch` (Node tests with relative URLs) — resolves
 * to the empty config so the caller keeps its page-origin fallback.
 */
export function loadRuntimeConfig(): Promise<RuntimeConfig> {
  return Promise.resolve()
    .then(() => fetch(RUNTIME_CONFIG_PATH, { cache: "no-store", signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) }))
    .then((response) => (response.ok ? (response.json() as Promise<unknown>) : undefined))
    .then((body) => parseRuntimeConfig(body))
    .catch(() => EMPTY_RUNTIME_CONFIG);
}

/**
 * Strict-validate the known fields; unknown keys are ignored (the contract
 * ships lockstep with the bundle, and lenient-parse/strict-validate keeps
 * future surfaces from bricking older bundles). Any known field that is
 * present but invalid voids the whole config (reject, don't sanitize).
 */
function parseRuntimeConfig(body: unknown): RuntimeConfig {
  if (typeof body !== "object" || body === null || Array.isArray(body)) return EMPTY_RUNTIME_CONFIG;
  const record = body as Record<string, unknown>;
  if (!validOptionalIssuer(record.idaasIssuer) || !validOptionalLabel(record.environment)) {
    return EMPTY_RUNTIME_CONFIG;
  }
  const config: { idaasIssuer?: string; environment?: string } = {};
  if (typeof record.idaasIssuer === "string") config.idaasIssuer = record.idaasIssuer;
  if (typeof record.environment === "string") config.environment = record.environment;
  return config;
}

/** Same shape rule as the explicit `issuer` config (origin-only http(s) URL). */
function validOptionalIssuer(value: unknown): boolean {
  if (value === undefined) return true;
  if (typeof value !== "string" || new TextEncoder().encode(value).length > MAX_FIELD_BYTES) return false;
  try {
    const parsed = new URL(value);
    return (parsed.protocol === "https:" || parsed.protocol === "http:") && parsed.origin === value;
  } catch {
    return false;
  }
}

function validOptionalLabel(value: unknown): boolean {
  if (value === undefined) return true;
  if (typeof value !== "string" || value.length === 0) return false;
  return new TextEncoder().encode(value).length <= MAX_FIELD_BYTES;
}
